import {createAction} from '@packages/utilities/create-action';
import {ContentEditorActive} from '@packages/shared/states';
import {
  type CONTENT_EDITOR_ADD_TYPES,
  ADD_AI_PARAGRAPH,
  ADD_COMPONENT,
  ADD_MEDIA,
  ADD_OL,
  ADD_UL,
  CONTENT_EDITOR_MENUS,
} from '@packages/systems/ContentEditor';
import type {RichTextComponentSource} from '@packages/shared/types/ContentEditor';
import {TOOLBARS} from '@packages/shared/constants/ContentEditor';
import type {IframeFigureData} from './types';
import {
  CONTENT_EDITOR_ELEMENT_ADDED,
  CONTENT_EDITOR_ELEMENT_INSERTED,
  CONTENT_EDITOR_COMPONENT_REMOVED,
  CONTENT_EDITOR_COMPONENT_ADDED,
  CONTENT_EDITOR_FIGURE_LINK_REMOVED,
  CONTENT_EDITOR_FIGURE_TITLE_UPDATED,
  CONTENT_EDITOR_KEY_UP,
} from '@packages/systems/designer/actions';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {Component} from '@packages/systems/wfdl';
import type {RangeSelectionRecordType} from '@packages/shared/records';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {
  AI_CONTENT_GENERATION_ENTRY,
  AI_PROMPT_MENU_ENTRY,
} from '@packages/systems/ai/writingAssistant';
import {ParsedSelectionType} from '@packages/shared/types/ContentEditor';
import type {ComponentMetadata} from '@packages/systems/component-utils';
import type {ComponentValidationFailureReason} from './validationMessages';

// Action Constants
export const CONTENT_EDITOR_MENU_TOGGLED =
  'CONTENT_EDITOR_MENU_TOGGLED' as const;
export const CONTENT_EDITOR_ADD_PLUS_BUTTON_CLICKED =
  'CONTENT_EDITOR_ADD_PLUS_BUTTON_CLICKED' as const;
export const CONTENT_EDITOR_SLASH_COMMAND_ACTIVATED =
  'CONTENT_EDITOR_SLASH_COMMAND_ACTIVATED' as const;
export const CONTENT_EDITOR_ADD_MENU_FOCUS_TOGGLED =
  'CONTENT_EDITOR_ADD_MENU_FOCUS_TOGGLED' as const;
export const CONTENT_EDITOR_AI_TOGGLE_OPTION =
  'CONTENT_EDITOR_AI_TOGGLE_OPTION' as const;
export const CONTENT_EDITOR_AI_PROMPT_SUBMITTED =
  'CONTENT_EDITOR_AI_PROMPT_SUBMITTED' as const;
export const CONTENT_EDITOR_AI_PROMPT_QUICK_MOD_MENU_TOGGLED =
  'CONTENT_EDITOR_AI_PROMPT_QUICK_MOD_MENU_TOGGLED' as const;
export const CONTENT_EDITOR_AI_FEEDBACK_MODAL_TOGGLED =
  'CONTENT_EDITOR_AI_FEEDBACK_MODAL_TOGGLED' as const;
export const CONTENT_EDITOR_AI_FIELD_GENERATION_REQUESTED =
  'CONTENT_EDITOR_AI_FIELD_GENERATION_REQUESTED' as const;
export const CONTENT_EDITOR_AI_FIELD_GENERATION_SUCCEEDED =
  'CONTENT_EDITOR_AI_FIELD_GENERATION_SUCCEEDED' as const;
export const CONTENT_EDITOR_AI_FIELD_GENERATION_FAILED =
  'CONTENT_EDITOR_AI_FIELD_GENERATION_FAILED' as const;
export const CONTENT_EDITOR_AI_TOOLBAR_POSITION_SET =
  'CONTENT_EDITOR_AI_TOOLBAR_POSITION_SET' as const;
export const CONTENT_EDITOR_INLINE_LIST_ADDED =
  'CONTENT_EDITOR_INLINE_LIST_ADDED' as const;
export const CONTENT_EDITOR_VIDEO_UPDATED =
  'CONTENT_EDITOR_VIDEO_UPDATED' as const;
export const AI_PROMPT_MENU_ON_SELECTION_OPENED =
  'AI_PROMPT_MENU_ON_SELECTION_OPENED' as const;
export const CONTENT_EDITOR_SYNCED = 'CONTENT_EDITOR_SYNCED' as const;
export const CONTENT_EDITOR_COMPONENT_VALIDATION_FAILED =
  'CONTENT_EDITOR_COMPONENT_VALIDATION_FAILED' as const;
export const CONTENT_EDITOR_PASTE_COMPONENTS_STRIPPED =
  'CONTENT_EDITOR_PASTE_COMPONENTS_STRIPPED' as const;

// Payload types
export type ContentEditorMenuToggledPayload = {
  menu:
    | (typeof CONTENT_EDITOR_MENUS)[keyof typeof CONTENT_EDITOR_MENUS]
    | typeof TOOLBARS.MEDIA
    | typeof TOOLBARS.COMPONENT
    | null;
};

export type ContentEditorAddMenuFocusSetPayload = {
  setFocus: boolean;
};

export type AiOptionSwitchedPayload = {
  seedId: string;
  direction: 'previous' | 'next';
};

export type AiPromptSubmittedPayload = {
  seedId: string;
  prompt: string;
  focusedSymbolNativeIdPath: NativeId[];
};

export type AiFieldGenerationRequestedPayload = {
  requestId: string;
  seedId: string;
  prompt: ContentEditorActive['aiData']['userPrompt'];
  content: string;
  outputFormat: 'RichText' | 'PlainText';
  generationEntry: AI_CONTENT_GENERATION_ENTRY;
  isQuickModPrompt: boolean;
};

export type AiToolbarPositionSetPayload = {
  rangeClientRect: RangeSelectionRecordType['rangeClientRect'];
};

export type InlineListAddedPayload = {
  seedId: string;
  addType: typeof ADD_UL | typeof ADD_OL;
};

export type FigureTitleUpdatedPayload = {
  figureTitle: string;
};

export type VideoUpdatedPayload = IframeFigureData;

export type AiFieldGenerationSucceededResponse = {
  choices: Array<string>;
  outputFormat: 'RichText' | 'PlainText';
};

export type AiEditMenuOnSelectionOpenedPayload = {
  seedId: string;
  outputFormat: 'RichText' | 'PlainText' | null;
  promptMenuEntry: AI_PROMPT_MENU_ENTRY;
};

type BaseElementAddedPayload = {
  guid: string;
};

type OtherElementPayload = BaseElementAddedPayload & {
  addType: Exclude<
    CONTENT_EDITOR_ADD_TYPES,
    typeof ADD_MEDIA | typeof ADD_AI_PARAGRAPH | typeof ADD_COMPONENT
  >;
};

type VideoElementPayload = BaseElementAddedPayload & {
  addType: typeof ADD_MEDIA;
  iframeFigureData: IframeFigureData;
};

type AiParagraphPayload = BaseElementAddedPayload & {
  addType: typeof ADD_AI_PARAGRAPH;
  // we need focusedSymbolNativeIdPath to generate the HTML context from the symbol.
  focusedSymbolNativeIdPath: NativeId[];
};

type ComponentPayload = BaseElementAddedPayload & {
  addType: typeof ADD_COMPONENT;
  componentType: ComponentMetadata['componentType'];
  targetNodeId?: string;
  source?: RichTextComponentSource;
};

export type InsertElementPayload = {
  element: ExpressionElement;
  componentMapPatch?: Component.ComponentMap;
  source?: RichTextComponentSource;
};

export type ElementAddedPayload =
  | OtherElementPayload
  | VideoElementPayload
  | AiParagraphPayload
  | ComponentPayload;

export type KeyUpPayload = {
  key: number;
  parsedSelection: ParsedSelectionType;
  metaKey: boolean;
  guid: string;
};

type FigureLinkRemovedPayload = {
  figureId: string;
};

// Matches the payload for NODE_TEXT_EDITING_ENDED so they can be used interchangeably in componentMapReducer.
export type ContentEditorSyncedPayload = string;

export type ComponentRemovedPayload = {
  componentIds: string[];
};

export type ComponentAddedPayload = {
  componentIds: string[];
  source?: RichTextComponentSource;
};

export type ComponentValidationFailedPayload = {
  errorMessage: string;
  reason?: ComponentValidationFailureReason;
};

// Action Creators
export const contentEditorMenuToggled = createAction<
  typeof CONTENT_EDITOR_MENU_TOGGLED,
  ContentEditorMenuToggledPayload
>(CONTENT_EDITOR_MENU_TOGGLED);

export const slashCommandActivated = createAction<
  typeof CONTENT_EDITOR_SLASH_COMMAND_ACTIVATED,
  void
>(CONTENT_EDITOR_SLASH_COMMAND_ACTIVATED);

export const contentEditorAddPlusButtonClicked = createAction<
  typeof CONTENT_EDITOR_ADD_PLUS_BUTTON_CLICKED,
  void
>(CONTENT_EDITOR_ADD_PLUS_BUTTON_CLICKED);

export const contentEditorAddMenuFocusSet = createAction<
  typeof CONTENT_EDITOR_ADD_MENU_FOCUS_TOGGLED,
  ContentEditorAddMenuFocusSetPayload
>(CONTENT_EDITOR_ADD_MENU_FOCUS_TOGGLED);

export const aiFieldGenerationRequested = createAction<
  typeof CONTENT_EDITOR_AI_FIELD_GENERATION_REQUESTED,
  AiFieldGenerationRequestedPayload
>(CONTENT_EDITOR_AI_FIELD_GENERATION_REQUESTED);

export const aiOptionSwitched = createAction<
  typeof CONTENT_EDITOR_AI_TOGGLE_OPTION,
  AiOptionSwitchedPayload
>(CONTENT_EDITOR_AI_TOGGLE_OPTION);

export const aiToolbarPositionSet = createAction<
  typeof CONTENT_EDITOR_AI_TOOLBAR_POSITION_SET,
  AiToolbarPositionSetPayload
>(CONTENT_EDITOR_AI_TOOLBAR_POSITION_SET);

export const aiPromptSubmitted = createAction<
  typeof CONTENT_EDITOR_AI_PROMPT_SUBMITTED,
  AiPromptSubmittedPayload
>(CONTENT_EDITOR_AI_PROMPT_SUBMITTED);

export const aiPromptQuickModMenuToggled = createAction<
  typeof CONTENT_EDITOR_AI_PROMPT_QUICK_MOD_MENU_TOGGLED,
  void
>(CONTENT_EDITOR_AI_PROMPT_QUICK_MOD_MENU_TOGGLED);

export const aiFeedbackModalToggled = createAction<
  typeof CONTENT_EDITOR_AI_FEEDBACK_MODAL_TOGGLED,
  void
>(CONTENT_EDITOR_AI_FEEDBACK_MODAL_TOGGLED);

export const contentEditorInlineListAdded = createAction<
  typeof CONTENT_EDITOR_INLINE_LIST_ADDED,
  InlineListAddedPayload
>(CONTENT_EDITOR_INLINE_LIST_ADDED);

export const contentEditorVideoUpdated = createAction<
  typeof CONTENT_EDITOR_VIDEO_UPDATED,
  VideoUpdatedPayload
>(CONTENT_EDITOR_VIDEO_UPDATED);

export const figureTitleUpdated = createAction<
  typeof CONTENT_EDITOR_FIGURE_TITLE_UPDATED,
  FigureTitleUpdatedPayload
>(CONTENT_EDITOR_FIGURE_TITLE_UPDATED);

export const aiEditMenuOnSelectionOpened = createAction<
  typeof AI_PROMPT_MENU_ON_SELECTION_OPENED,
  AiEditMenuOnSelectionOpenedPayload
>(AI_PROMPT_MENU_ON_SELECTION_OPENED);

export const contentEditorElementAdded = createAction<
  typeof CONTENT_EDITOR_ELEMENT_ADDED,
  ElementAddedPayload
>(CONTENT_EDITOR_ELEMENT_ADDED);

export const contentEditorElementInserted = createAction<
  typeof CONTENT_EDITOR_ELEMENT_INSERTED,
  InsertElementPayload
>(CONTENT_EDITOR_ELEMENT_INSERTED);

export const keyUp = createAction<typeof CONTENT_EDITOR_KEY_UP, KeyUpPayload>(
  CONTENT_EDITOR_KEY_UP
);

export const figureLinkRemoved = createAction<
  typeof CONTENT_EDITOR_FIGURE_LINK_REMOVED,
  FigureLinkRemovedPayload
>(CONTENT_EDITOR_FIGURE_LINK_REMOVED);

export const contentEditorSynced = createAction<
  typeof CONTENT_EDITOR_SYNCED,
  ContentEditorSyncedPayload
>(CONTENT_EDITOR_SYNCED);

export const contentEditorComponentRemoved = createAction<
  typeof CONTENT_EDITOR_COMPONENT_REMOVED,
  ComponentRemovedPayload
>(CONTENT_EDITOR_COMPONENT_REMOVED);

export const contentEditorComponentAdded = createAction<
  typeof CONTENT_EDITOR_COMPONENT_ADDED,
  ComponentAddedPayload
>(CONTENT_EDITOR_COMPONENT_ADDED);

export const contentEditorComponentValidationFailed = createAction<
  typeof CONTENT_EDITOR_COMPONENT_VALIDATION_FAILED,
  ComponentValidationFailedPayload
>(CONTENT_EDITOR_COMPONENT_VALIDATION_FAILED);

export const contentEditorPasteComponentsStripped = createAction<
  typeof CONTENT_EDITOR_PASTE_COMPONENTS_STRIPPED,
  void
>(CONTENT_EDITOR_PASTE_COMPONENTS_STRIPPED);
